import { createContext, ReactNode, useContext } from 'react';
import { ExactLimitsBoard } from '../../api/exactLimitsBoard';
import { ReadinessState } from '../../api/rollout';
import { clockSeconds, span } from './rolloutModel';

const every = (seconds: number) => span(seconds * 1000);

/**
 * Whether the readings on a page still come: false for a watch that is over or stopped, so its
 * cards show how old their data is instead of waiting for a next reading.
 */
export const ReadingsLive = createContext(true);

/**
 * How old what a card shows is: "gelezen 4s geleden · elke 10s". Once the next reading is due a
 * spinner replaces the dot and the age keeps counting; orange when that takes three rounds. A
 * finished watch only says how old its last reading is.
 */
export const Fresh = ({
    at,
    every: seconds,
    now,
    label = 'gelezen',
}: {
    at?: string | null;
    every: number;
    now: number;
    label?: string;
}) => {
    const live = useContext(ReadingsLive);

    if (!at) {
        return live ? (
            <span className="ro-fresh is-busy">
                <span className="ro-fresh__spinner" aria-hidden />
                ophalen…
            </span>
        ) : (
            <span className="ro-fresh">niet gelezen</span>
        );
    }

    const age = Math.max(0, now - Date.parse(at));
    const due = live && seconds > 0 && age >= seconds * 1000;
    // Late after three rounds, and never before a round plus fifteen seconds: a round that also asks Chargebee takes a while.
    const late = due && age > Math.max(seconds * 3, seconds + 15) * 1000;
    const title = `${label} om ${clockSeconds(at)}${
        live && seconds > 0 ? `, elke ${every(seconds)}` : ''
    }`;

    return (
        <span
            className={`ro-fresh${
                due ? ' is-busy' : age < 1500 ? ' is-new' : ''
            }${late ? ' is-late' : ''}`}
            title={title}
        >
            {due ? (
                <span className="ro-fresh__spinner" aria-hidden />
            ) : (
                <span className="ro-fresh__dot" aria-hidden />
            )}
            {label} {span(age)} geleden
            {live && !due && seconds > 0 && (
                <span className="ro-fresh__every">
                    {' '}
                    · elke {every(seconds)}
                </span>
            )}
        </span>
    );
};

const ICON: Record<ReadinessState, string> = {
    Ok: '✓',
    Warn: '!',
    Bad: '✕',
    Unknown: '?',
};

export const STATE_LABEL: Record<ReadinessState, string> = {
    Ok: 'in orde',
    Warn: 'let op',
    Bad: 'niet in orde',
    Unknown: 'onbekend',
};

/** A state as a round icon; the label is there for screen readers and as tooltip. */
export const StateIcon = ({ state }: { state: ReadinessState }) => (
    <span
        className={`ro-state ro-state--${state.toLowerCase()}`}
        role="img"
        aria-label={STATE_LABEL[state]}
        title={STATE_LABEL[state]}
    >
        {ICON[state]}
    </span>
);

/** One row of a check list: icon, label, one line of detail, optionally an action and more below. */
export const CheckRow = ({
    state,
    label,
    detail,
    action,
    children,
}: {
    state: ReadinessState;
    label: string;
    detail?: ReactNode;
    action?: ReactNode;
    children?: ReactNode;
}) => (
    <li className="ro-check">
        <StateIcon state={state} />
        <span className="ro-check__label">{label}</span>
        {action && <span className="ro-check__action">{action}</span>}
        {detail && <span className="ro-check__detail">{detail}</span>}
        {children && <div className="ro-check__more">{children}</div>}
    </li>
);

/** A card with a title, an optional aside on the right and its content. */
export const Card = ({
    title,
    aside,
    tone,
    children,
    footer,
}: {
    title?: string;
    aside?: ReactNode;
    tone?: 'ok' | 'warn' | 'bad';
    children: ReactNode;
    footer?: ReactNode;
}) => (
    <section
        className={`ro-card${tone ? ` ro-card--${tone}` : ''}`}
        aria-label={title}
    >
        {(title || aside) && (
            <header className="ro-card__head">
                {title && <h3>{title}</h3>}
                {aside && <span className="ro-card__aside">{aside}</span>}
            </header>
        )}
        {children}
        {footer && <div className="ro-card__foot">{footer}</div>}
    </section>
);

/** One figure on a tile: a label, the number and a line to compare it with. */
export const Kpi = ({
    label,
    value,
    sub,
    tone,
}: {
    label: string;
    value: ReactNode;
    sub?: ReactNode;
    tone?: 'ok' | 'warn' | 'bad';
}) => (
    <div className={`ro-kpi${tone ? ` ro-kpi--${tone}` : ''}`}>
        <div className="ro-kpi__label">{label}</div>
        <div className="ro-kpi__value">{value}</div>
        {sub && <div className="ro-kpi__sub">{sub}</div>}
    </div>
);

const count = (n: number) => n.toLocaleString('nl-NL');

const Meter = ({
    label,
    remaining,
    limit,
    resetsAt,
    now,
}: {
    label: string;
    remaining: number;
    limit: number;
    resetsAt?: string | null;
    now: number;
}) => {
    const left = limit > 0 ? Math.max(0, Math.min(1, remaining / limit)) : 0;
    const tone = left <= 0.1 ? ' is-bad' : left <= 0.25 ? ' is-warn' : '';
    const reset = resetsAt ? Date.parse(resetsAt) : undefined;

    return (
        <div className="ro-meter">
            <div className="ro-meter__head">
                <span>{label}</span>
                <strong>
                    {count(remaining)} / {count(limit)}
                </strong>
            </div>
            <div
                className="ro-meter__track"
                role="progressbar"
                aria-label={`${label}: over`}
                aria-valuenow={Math.round(left * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <div
                    className={`ro-meter__fill${tone}`}
                    style={{ width: `${left * 100}%` }}
                />
            </div>
            {reset !== undefined && reset > now && (
                <div className="ro-meter__sub">
                    weer vol over {span(reset - now)}
                </div>
            )}
        </div>
    );
};

/** Exact's day and minute as the connector last saw them; nothing before the first reading. */
export const ExactMeters = ({
    board,
    now,
}: {
    board?: ExactLimitsBoard | null;
    now: number;
}) => {
    if (!board || board.dailyRemaining == null)
        return <p className="ro-muted">Nog geen stand van Exact.</p>;

    const minuteFresh = board.minutelyResetsAtUtc
        ? Date.parse(board.minutelyResetsAtUtc) > now
        : false;

    return (
        <>
            <Meter
                label="Vandaag"
                remaining={board.dailyRemaining}
                limit={board.dailyLimit ?? 5000}
                resetsAt={board.dailyResetsAtUtc}
                now={now}
            />
            <Meter
                label="Deze minuut"
                remaining={
                    minuteFresh
                        ? board.minutelyRemaining ?? 60
                        : board.minutelyLimit ?? 60
                }
                limit={board.minutelyLimit ?? 60}
                resetsAt={minuteFresh ? board.minutelyResetsAtUtc : undefined}
                now={now}
            />
        </>
    );
};

/** The 48 hours of a watch: a day before the deploy, a day after, and where now is. */
export const WatchWindow = ({
    deployedAt,
    endsAt,
    now,
}: {
    deployedAt: string;
    endsAt: string;
    now: number;
}) => {
    const from = Date.parse(deployedAt) - 24 * 3600 * 1000;
    const to = Date.parse(endsAt);
    const at = Math.max(0, Math.min(1, (now - from) / (to - from)));

    return (
        <div className="ro-window" aria-label="Venster van de wacht">
            <div className="ro-window__track">
                <div
                    className="ro-window__fill"
                    style={{ width: `${at * 100}%` }}
                />
                <div className="ro-window__deploy" />
            </div>
            <div className="ro-window__labels">
                <span>24 u vóór</span>
                <span>deploy</span>
                <span>{now < to ? `nog ${span(to - now)}` : 'klaar'}</span>
            </div>
        </div>
    );
};
